import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, effect, inject, model, output, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ChipModule } from 'primeng/chip';
import { InputGroup } from "primeng/inputgroup";
import { InputGroupAddon } from "primeng/inputgroupaddon";
import { MultiSelectModule } from 'primeng/multiselect';
import { SkeletonModule } from 'primeng/skeleton';
import { ColumnService } from '../../../@core/services/column.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { UiLocaleService } from '../../../@core/services/i18n/ui-locale.service';
import { ServerConfigService } from '../../../data/server-config';
import { CustomFieldConfig, CustomFields } from '../../../generated/generated-types';

export interface PrefixOptions {
  label: string;
  fieldName: string;
}

@Component({
  selector: 'app-entity-property-dropdown',
  imports: [
    CommonModule,
    // PhxSelect,
    MultiSelectModule,
    FormsModule,
    InputGroup,
    InputGroupAddon,
    SkeletonModule,
    ChipModule,
  ],
  templateUrl: './entity-property-dropdown.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityPropertyDropdownComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, ControlValueAccessor {
  entityName = model<string>('');

  items = model<{ label: string, value: string, type: string }[]>([]);
  value = model<string[]>([]);

  stringsOnly = model<boolean>(true);

  outputPrefix = model<PrefixOptions>();

  onValueChange = output<string[]>();

  disabled = model<boolean>(false);

  includeCustomFields = model<boolean>(false);

  isInitialized = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private columnService: ColumnService,
    private i18nService: I18nService,
    private uiLocaleService: UiLocaleService,
    protected serverConfigService: ServerConfigService,
  ) {
    super();
    effect(() => {
      if (this.entityName()) {
        this.loadData();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.props?.entityName) {
      this.entityName.set(this.props.entityName);
    }
    if (this.props?.includeCustomFields) {
      this.includeCustomFields.set(this.props.includeCustomFields);
    }
    if (this.value()?.length > 0 && this.formControl && (this.formControl?.value?.length ?? 0) == 0) {
      this.formControl.setValue(this.value());
    }
    // valueChanges must be torn down; recreating the field stacked listeners otherwise.
    this.formControl?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.value.set(value);
      });
  }

  async loadData() {
    let items: { label: string, value: string, type: string }[] = [];
    let columns = await this.columnService.getAllColumnsOfType(this.entityName(), false)
    if (this.props?.stringsOnly ?? this.stringsOnly()) {
      columns = columns.filter(x => x.dataType.toLowerCase() === "string" && !this.isAnIdField(x))
    }

    items.push(...columns?.map(x => ({ label: this.i18nService.translate(this.entityName() + '.' + (x.prop ?? x.field)), value: x.prop ?? x.field, type: x.dataType }))?.sort((a, b) => a.label.localeCompare(b.label)));

    if (this.includeCustomFields() || this.outputPrefix()) {
      let customFields = this.serverConfigService.getCustomFieldsFor(this.entityName() as Exclude<keyof CustomFields, '__typename'>);
      if (this.props?.stringsOnly ?? this.stringsOnly()) {
        customFields = customFields.filter(x => x.type === 'string');
      }
      items.push(...customFields?.map(x => ({ label: this.i18nService.translateCustomFieldFromConfig(x), value: "customFields." + x.name, type: x.type }))?.sort((a, b) => a.label.localeCompare(b.label)));
    }

    items = items.filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i); //remove duplicates

    this.items.set(items);
    this.isInitialized = true;
  }

  getCustomFieldLabel(customField: CustomFieldConfig): string {
    if (!customField.label?.length) {
      let translationKey = 'columns.customFields.' + customField.name;
      let translation = this.i18nService.translate(translationKey);
      if (translation && translation !== translationKey)
        return typeof translation === 'string' ? translation : ((translation as any)._ ?? customField.name);
      return customField.name;
    }
    let lang = this.uiLocaleService.language;
    let label = customField.label?.find(x => x.languageCode == lang);
    if (label)
      return label.value;
    label = customField.label?.find(x => x.languageCode.toLowerCase() == "de");
    if (label)
      return label.value;
    return customField.label?.[0]?.value;
  }

  isAnIdField(column: { prop?: string, field?: string }): boolean {
    let value = (column.prop ?? column.field);
    return value == "id" || value.endsWith("Id");
  }

  getItemForValue(value: string): { label: string, value: string, type: string } {
    return this.items().find(x => x.value === value);
  }

  unselectItem(value: string): void {
    this.value.set(this.value().filter(x => x !== value));
    this.onValueChange.emit(this.value());
  }

  //#region ControlValueAccessor
  onChange: (value: string[]) => void;
  onTouched: () => void;

  writeValue(value: string[]): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  //for formly:
  valueChange(event: { value: string[] }) {
    this.formControl?.setValue(event.value);
    this.onChange?.(event.value);
    this.onValueChange.emit(event.value);
  }
  //#endregion
}
